import * as React from 'react'
import * as Diff from 'diff'
import _cloneDeep from 'lodash.clonedeep'
import _omit from 'lodash.omit'
import { TreeManager } from '@basehub/utils/tree/index'
import {
  type BlockByType,
  type Block,
  type BlockChange,
  type RichTextBlock,
  type ReferenceBlock,
  type InstanceBlock,
  type BlockChangeChildStatus,
  blockTypesRecord,
  isHidableBlock,
  isCommentBlock,
  ContentRequest,
  BlockChangeWithConflict,
  isTypeForBlockWithChildren,
  VariantSetBlock,
  normalizeStringPath,
} from '@basehub/data/replicache/index'
import { Input } from '~/sotano-kit/primitives/input'
import { clsx } from 'clsx'
import { StatusIcon } from '~/components/icons/status'
import { BooleanBlockDiff } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/boolean'
import { DiffCodeEditor } from '~/sotano-kit/primitives/code-editor'
import { Language } from '@basehub/shared-constants'
import { RichTextBlockDiff } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/rich-text'
import { DiffBlockTitle } from './block-title'
import { ReferenceBlockDiff } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/reference'
import { DiffBlockWrapper } from './block-wrapper'
import { SelectBlockDiff } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/select'
import { getSelectDiff } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/select/diff'
import { ColorInputDiff } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/color'
import {
  commentExtensionsForTransformations,
  isEmpty,
  jsonToPlainText,
  stateToJson,
} from '@basehub/tiptap-api-utils'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import { ListBlockDiff } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/list/diff'
import { NestedDocumentBlockLink } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/document/nested/link'
import { useRepoStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/repo-store'
import { DiffAsset } from './asset'
import { DiffLine } from './line'
import { ArrowLeftIcon, ArrowRightIcon, ResetIcon } from '@radix-ui/react-icons'
import {
  useGetTabPrefix,
  useTabsStore,
} from '../../../(editor)/explore/components/tabs/store'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'

import { OgImageBlockInputDiff } from '../../../(editor)/explore/components/blocks/og-image/diff'
import { PropertiesDiff } from '../properties-diff'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import {
  TextBlockDiff,
  TextOverrideDiff,
} from '../../../(editor)/explore/components/blocks/text/diff'
import {
  useBranchOrCommitSegment,
  useSelectedBranch,
} from '~/lib/commits/use-selected-branch-or-commit'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import {
  VariantSetBlockDiff,
  getVariantsDiff,
} from '../../../(editor)/explore/components/blocks/variant-set/diff'
import { DateBlockDiff } from '../../../(editor)/explore/components/blocks/date/diff'
import { useIsExternalVisitor } from '~/hooks-data/use-user-role'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { WorkflowBlockDiff } from '../../../(editor)/explore/components/blocks/workflow/diff'
import { IconBlockDiff } from '../../../(editor)/explore/components/blocks/icon/diff'
import { BlockIconStoreProvider } from '~/components/block-icon/store'
import { ContributorsTooltip } from '~/components/contributors-tooltip'
import { flattenContributors } from '@basehub/data/replicache/schema/block-milestone/util'
import { ContributorsHug } from '../../../(editor)/history/_components/sidebar/primitive-commit'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { PasteBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/paste'
import { getId } from '@basehub/utils/id/index'
import { isErrorLike } from '@basehub/utils/error'
import { toast } from 'sonner'
import { copyBlockToClipboard } from '~/hooks-data/copy-paste'
import { useCustomSyncerSub } from '~/replicache/frontend/use-custom-syncer-sub'

// lazy imports
const CommentsInBlock = React.lazy(
  () =>
    import(
      '../../../(editor)/explore/components/blocks/block-wrapper-actions/comments-in-block' /* webpackChunkName: "comments-in-block" */
    )
)

export type DiffChildBlockProps = {
  statusFromParent: BlockChangeChildStatus
  child: BlockChange
  index: number
  changesTree: TreeManager<BlockChange, NonNullable<unknown>> | undefined
  treeNew: TreeManager<Block, NonNullable<unknown>> | undefined
  treeOld: TreeManager<Block, NonNullable<unknown>> | undefined
  isInFullView?: boolean
  displayBlockLink?: boolean
  commentsControls?: boolean
  diffTabPrefix?: string
  previewLink?: React.ReactNode
}

export const DiffChildBlock = React.memo(
  ({
    child,
    index,
    changesTree,
    treeNew,
    treeOld,
    statusFromParent,
    isInFullView = false,
    displayBlockLink = true,
    diffTabPrefix = 'diff:',
    commentsControls = true,
    previewLink,
  }: DiffChildBlockProps) => {
    const gridRowStart = index + 1
    const change = child

    const workingTreeRef = useWorkingTreeStore((s) => s.treeRef)
    const addTab = useTabsStore((s) => s.addTab)
    const setFocus = useRepoStore((s) => s.setFocus)
    const { teamSlug, repoSlug, repoPage } = useDashboardSlugs()
    const { encodedSegment } = useBranchOrCommitSegment()
    const isExternalVisitor = useIsExternalVisitor()
    const { repoId, replicache } = useRepoReplicache().data ?? {}

    const { cr } = useSelectedBranch()

    const { data: blockMilestones } = useCustomSyncerSub(replicache, {
      syncer: 'multiBlockMilestones',
      params: {
        rootId: treeNew?.root.value.id || treeOld?.root.value.id || '',
        sourceBlockIds: [idToSourceBlockId(child)],
      },
    })
    const blockMilestone = blockMilestones?.[0] ?? null

    const ReorderPointer = React.useCallback(
      ({ condition }: { condition: 'removed' | 'added' }) => {
        if (change.status !== 'reordered') return null
        if (!changesTree?.itemsMap) return null

        const parent = Array.from(changesTree.itemsMap.values()).find((node) =>
          node.value.children?.find(
            (child) =>
              child.id === change.id && child.status !== statusFromParent
          )
        )
        if (!parent) return null

        const parentIsRootBlock = parent.value.type === 'root'

        if (parentIsRootBlock) {
          return (
            <div className="bg-blue/10 text-blue border border-blue/40 whitespace-nowrap rounded-md min-h-12 flex items-center justify-center h-full w-full text-xs cursor-default">
              Reordered {condition === 'removed' ? 'from' : 'to'}&nbsp;
              {parent.value.title}
            </div>
          )
        }

        return (
          <Button
            unstyled
            focusStyled
            className="hover:bg-blue/15 bg-blue/10 text-blue border border-blue/40 min-h-12 whitespace-nowrap rounded-md flex items-center justify-center h-full w-full text-xs cursor-default"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey) return
              addTab({ key: `diff:${parent.key}`, partiallyOpen: true })
              setFocus({ type: 'block:wrapper', blockId: change.id })
            }}
          >
            {condition === 'removed' && (
              <>
                <ArrowLeftIcon />
                &nbsp;
              </>
            )}
            Reordered {condition === 'removed' ? 'from' : 'to'}&nbsp;
            {parent.value.title}
            {condition === 'added' && (
              <>
                &nbsp;
                <ArrowRightIcon />
              </>
            )}
          </Button>
        )
      },
      [
        change.status,
        change.id,
        addTab,
        changesTree?.itemsMap,
        statusFromParent,
        setFocus,
      ]
    )

    const blockLastVersion = change.current ?? change.previous
    const [isHovered, setIsHovered] = React.useState(false)

    return (
      <BlockIconStoreProvider
        getChildrenKeys={changesTree?.getChildrenKeys}
        key={index}
      >
        {/* Left side (committed/old version) */}
        <div
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={clsx(
            change.status === 'conflict' && 'pr-8',
            'relative group/old-wrapper'
          )}
        >
          {statusFromParent === 'reordered' ||
          statusFromParent === 'created' ? (
            <DiffBlockWrapper id={change.id}>
              <div className="h-9 flex items-center" />
              <ReorderPointer condition="removed" />
            </DiffBlockWrapper>
          ) : (
            <BlockDiffSwitch
              id={`block-diff-${child.id}`}
              key={`old-${child.id}`}
              style={{ gridRowStart }}
              condition="removed"
              change={change}
              changesTree={changesTree}
              treeOld={treeOld}
              treeNew={treeNew}
              isInFullView={isInFullView}
              displayBlockLink={displayBlockLink}
              extraContentNode={({ wrapper }) => (
                <PropertiesDiff
                  wrapper={wrapper}
                  condition="removed"
                  change={change}
                  changesTree={changesTree}
                  treeOld={treeOld}
                  treeNew={treeNew}
                  isInFullView={isInFullView}
                />
              )}
            >
              <div className="h-9 flex items-center" />
            </BlockDiffSwitch>
          )}
          {['modified', 'deleted'].includes(child.status) && repoId && (
            <Button
              unstyled
              focusStyled
              icon={
                <ResetIcon height={15} width={15} className="-translate-y-px" />
              }
              onClick={async (e) => {
                e.stopPropagation()
                e.preventDefault()
                if (!repoId || !replicache) return
                if (!workingTreeRef.current || !child.previous) return

                // We'll try to get the working block and paste the previous content on it
                const selectedNode = workingTreeRef.current.getNode(
                  idToSourceBlockId(child.previous)
                )
                if (!selectedNode) {
                  if (!treeOld) {
                    return toast.error(
                      "Block couldn't be found in the working tree. This could happen if you're trying to recover deleted blocks.",
                      { duration: 6000 }
                    )
                  }

                  await copyBlockToClipboard({
                    onSuccess: () => {
                      toast.success(
                        `Block "${
                          child.previous?.title ?? 'Untitled'
                        }" copied to clipboard.`
                      )
                    },
                    onError: () => {
                      toast.error(
                        `Could not copy block "${
                          child.previous?.title ?? 'Untitled'
                        }" to clipboard.`
                      )
                    },
                    blockId: child.previous.id,
                    blockTreeManager: treeOld,
                  })
                  return
                }
                const selectedBlock = selectedNode.value
                const path = workingTreeRef.current.getPath(
                  idToSourceBlockId(child.previous),
                  { required: true }
                )

                const selectedBlockIsACommentWithNoContent = selectedBlock
                  ? isCommentBlock(selectedBlock) &&
                    isEmpty(selectedBlock.value.state)
                  : false

                try {
                  const params: PasteBlockParams = {
                    type: 'copy',
                    pastedBlocksData: [{ ...child.previous, relativePath: [] }],
                    supportBlocksData: [],
                    rootId: workingTreeRef.current.root.value.id,
                    position: { type: 'last' },
                    path,
                    selectedBlock: {
                      id: selectedBlock.id,
                      type: selectedBlock.type,
                      path,
                      instanceChildTarget: selectedBlock.instanceChildTarget,
                    },
                    isoNow: new Date().toISOString(),
                    duplicatedComponentStrategy: 'create-instance',
                    idSeed: getId(),
                    repoId,
                    ...(selectedBlockIsACommentWithNoContent && {
                      replaceBlockId: selectedBlock?.id ?? undefined,
                    }),
                    replaceValueOnly: true,
                    shiftKeyPressed: true,
                  }
                  // const pasteBlockResult =
                  await replicache.mutate.pasteBlock(params).then(() => {
                    if (selectedNode.parentKey) {
                      addTab({
                        key: selectedNode.parentKey + '#' + selectedNode.key,
                        partiallyOpen: true,
                      })
                    }

                    toast.success(
                      `Block "${child.previous?.title ?? 'Untitled'}" recovered`
                    )
                  })

                  // return {
                  //   ...pasteBlockResult,
                  //   redo: {
                  //     op: 'pasteBlock',
                  //     args: params,
                  //   } satisfies RepoUndoOps[number],
                  // }
                } catch (error) {
                  console.error(error)
                  toast.error(
                    `Could not recover block: ${
                      isErrorLike(error)
                        ? error.message
                        : 'An unexpected error occurred'
                    }`
                  )
                }
              }}
              className="flex invisible opacity-0 z-tooltip absolute right-px top-2 text-xs group-hover/old-wrapper:visible group-hover/old-wrapper:opacity-100 items-center gap-x-0.5 text-dark-faint hover:text-dark-dim font-medium leading-normal cursor-default transition-[color,opacity]"
            >
              Recover
            </Button>
          )}
        </div>

        {/* Block change status indicator */}
        <div className="relative flex justify-center col-start-2 z-50">
          <div
            className={clsx(
              'sticky self-start size-3 top-4 mt-3 flex items-center justify-center gap-1',
              {
                '!mt-14':
                  isHidableBlock(blockLastVersion!) &&
                  change.status !== 'unmodified',
                '!mt-1.5': isCommentBlock(blockLastVersion!),
              }
            )}
          >
            {change.status === 'conflict' ? (
              <div className="absolute self-start mx-auto left-1/2 -translate-x-1/2 size-3 top-3 flex items-center justify-center gap-1">
                {cr && <ConflictResolutionControls cr={cr} change={change} />}
              </div>
            ) : isTypeForBlockWithChildren(child.type) ? (
              <ButtonLink
                unstyled
                focusStyled
                href={`/${teamSlug}/${repoSlug}/${repoPage}/${encodedSegment}/${diffTabPrefix}${child.id}`}
                className="block p-2 -m-2 hover:bg-dark-shade-hover hover:border-dark-border-solid border border-transparent rounded-full cursor-default shrink-0 flex-grow"
                onClick={() => {
                  addTab({
                    key: `${diffTabPrefix}${child.id}`,
                    partiallyOpen: true,
                  })
                }}
              >
                <StatusIcon
                  status={change.status}
                  className="bg-dark-base size-[15px]"
                  blameProps={{
                    sourceBlockId: idToSourceBlockId(change),
                    rootId: treeNew?.root.value.id ?? null,
                  }}
                />
              </ButtonLink>
            ) : (
              <div className="p-2 -m-2 rounded-full shrink-0 flex-grow">
                <StatusIcon
                  status={change.status}
                  className="bg-dark-base size-[15px]"
                  blameProps={{
                    sourceBlockId: idToSourceBlockId(change),
                    rootId: treeNew?.root.value.id ?? null,
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Right side (working/new version) */}
        <div
          className={clsx(
            change.status === 'conflict' && 'pl-8',
            'relative',
            blockMilestone?.contributors && '[&_.diff-title]:pr-20'
          )}
          onMouseLeave={() => setIsHovered(false)}
          onMouseEnter={() => setIsHovered(true)}
        >
          {statusFromParent === 'reordered-source' ? (
            <DiffBlockWrapper id={change.id}>
              <ReorderPointer condition="added" />
            </DiffBlockWrapper>
          ) : (
            <BlockDiffSwitch
              id={`block-diff-${child.id}`}
              key={`new-${child.id}`}
              style={{ gridRowStart, gridColumnStart: 3 }}
              condition="added"
              change={change}
              changesTree={changesTree}
              treeOld={treeOld}
              treeNew={treeNew}
              isInFullView={isInFullView}
              displayBlockLink={displayBlockLink}
              extraContentNode={({ wrapper }) => (
                <PropertiesDiff
                  wrapper={wrapper}
                  condition="added"
                  change={change}
                  changesTree={changesTree}
                  treeOld={treeOld}
                  treeNew={treeNew}
                  isInFullView={isInFullView}
                />
              )}
            >
              <div className="flex items-center gap-2 ml-auto justify-center h-9 absolute right-0">
                {blockMilestone?.contributors && (
                  <ContributorsTooltip
                    content={change.status}
                    blockMilestone={blockMilestone}
                  >
                    <ContributorsHug
                      disableTooltip
                      contributors={flattenContributors(
                        blockMilestone.contributors
                      )}
                    />
                  </ContributorsTooltip>
                )}
                {previewLink}
              </div>
            </BlockDiffSwitch>
          )}
          {commentsControls && !isInFullView && !isExternalVisitor && (
            <React.Suspense fallback={null}>
              <div
                className="absolute right-0 w-[64px] h-full top-0 z-20 translate-x-full pl-2"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <CommentsInBlock
                  wrapperClassName="!relative !inset-0 -mt-2"
                  type="diff"
                  block={(change.current ?? change.previous)!}
                  isHovered={isHovered}
                />
              </div>
            </React.Suspense>
          )}
        </div>
      </BlockIconStoreProvider>
    )
  }
)

type BlockDiffSwitchProps = React.HTMLAttributes<HTMLElement> & {
  condition: 'removed' | 'added'
  change: BlockChange
  treeNew: TreeManager<Block, NonNullable<unknown>> | undefined
  treeOld: TreeManager<Block, NonNullable<unknown>> | undefined
  changesTree: TreeManager<BlockChange, NonNullable<unknown>> | undefined
  isInFullView?: boolean
  displayBlockLink?: boolean
  extraContentNode?: ({
    wrapper,
    fallback,
  }: {
    fallback?: React.ReactNode
    wrapper: ({ children }: { children: React.ReactNode }) => React.ReactNode
  }) => React.ReactNode
}

const BlockDiffSwitch = ({
  change,
  condition,
  displayBlockLink,
  isInFullView,
  treeOld,
  treeNew,
  changesTree,
  children,
  extraContentNode,
  ...rest
}: BlockDiffSwitchProps) => {
  let type = change.type
  if (condition === 'added') {
    type = change.current?.type ?? change.type
  } else if (condition === 'removed') {
    type = change.previous?.type ?? change.type
  }
  const tabPrefix = useGetTabPrefix({ defaultValue: 'diff:' })
  const path = normalizeStringPath({
    path: change.path,
    includeEdge: false,
    edgeId: change?.current?.id,
    format: 'array',
  })
  const parentBlockId = path.pop()
  const parentSourceBlockId =
    parentBlockId && treeNew && treeNew.getNode(parentBlockId)
      ? idToSourceBlockId(parentBlockId, treeNew)
      : ''
  const titleAsLink = Boolean(
    condition === 'added' && displayBlockLink && parentSourceBlockId
  )

  switch (type) {
    case blockTypesRecord.text:
      return (
        <DiffBlockWrapper id={change.id} {...rest}>
          {isInFullView ? (
            <div className="h-9 flex items-center" />
          ) : (
            <DiffBlockTitle
              type={type}
              blockChange={change}
              condition={condition}
              link={titleAsLink}
              parentSourceBlockId={parentSourceBlockId}
            >
              {children}
            </DiffBlockTitle>
          )}
          <TextBlockDiff type={type} change={change} condition={condition} />
          {extraContentNode &&
            extraContentNode({
              wrapper: ({ children }) => <div className="mt-2">{children}</div>,
            })}
        </DiffBlockWrapper>
      )
    case blockTypesRecord.number: {
      const isDeletedAndConditionIsAdded =
        condition === 'added' && change.status === 'deleted'

      let previousValue = String(change.previous?.value ?? '')
      if (change.status === 'conflict') {
        const indexOfValueConflict = change.conflictPaths.findIndex(
          (p) => p === 'value'
        )
        if (indexOfValueConflict !== -1) {
          previousValue = String(
            change.conflictValues[indexOfValueConflict]?.head ?? ''
          )
        }
      }

      const diffValue = Diff.diffChars(
        previousValue,
        String(
          isDeletedAndConditionIsAdded
            ? (change.previous?.value ?? '')
            : (change.current?.value ?? '')
        )
      )

      return (
        <DiffBlockWrapper id={change.id} {...rest}>
          <DiffBlockTitle
            blockChange={change}
            condition={condition}
            link={titleAsLink}
            parentSourceBlockId={parentSourceBlockId}
          >
            {children}
          </DiffBlockTitle>
          <Input
            asChild
            className={clsx(isDeletedAndConditionIsAdded && 'opacity-30')}
          >
            <DiffLine
              diff={diffValue}
              condition={condition}
              className={clsx(
                condition === 'removed' && '!text-dark-muted',
                'py-2 !h-auto min-h-[39px]'
              )}
            />
          </Input>
          <TextOverrideDiff change={change} condition={condition} />
          {/*       {extraContentNode &&
            extraContentNode({
              wrapper: ({ children }) => <div className="mt-2">{children}</div>,
            })} */}
        </DiffBlockWrapper>
      )
    }
    case blockTypesRecord.ogImage: {
      return (
        <DiffBlockWrapper {...rest} id={change.id}>
          <DiffBlockTitle
            blockChange={change}
            condition={condition}
            link={titleAsLink}
            parentSourceBlockId={parentSourceBlockId}
          >
            {children}
          </DiffBlockTitle>
          <OgImageBlockInputDiff
            condition={condition}
            diff={change}
            path={change.path}
            treeNew={treeNew}
            treeOld={treeOld}
          />
          {extraContentNode &&
            extraContentNode({
              wrapper: ({ children }) => <div className="mt-2">{children}</div>,
            })}
        </DiffBlockWrapper>
      )
    }
    case blockTypesRecord.boolean: {
      return (
        <DiffBlockWrapper {...rest} id={change.id}>
          <DiffBlockTitle
            blockChange={change}
            condition={condition}
            link={titleAsLink}
            parentSourceBlockId={parentSourceBlockId}
          >
            {children}
          </DiffBlockTitle>
          <BooleanBlockDiff change={change} condition={condition} />
          {extraContentNode &&
            extraContentNode({
              wrapper: ({ children }) => <div className="mt-2">{children}</div>,
            })}
        </DiffBlockWrapper>
      )
    }
    case blockTypesRecord.list: {
      return (
        <DiffBlockWrapper id={change.id} {...rest}>
          {isInFullView ? null : (
            <DiffBlockTitle
              blockChange={change}
              condition={condition}
              link={titleAsLink}
              parentSourceBlockId={parentSourceBlockId}
            >
              {children}
            </DiffBlockTitle>
          )}
          <ListBlockDiff
            blockChange={change}
            treeManager={
              (condition === 'removed' || change.status === 'deleted'
                ? treeOld
                : treeNew) ?? null
            }
            fullView={isInFullView ?? false}
            condition={condition}
          />
          {extraContentNode &&
            extraContentNode({
              wrapper: ({ children }) => <div className="mt-2">{children}</div>,
            })}
        </DiffBlockWrapper>
      )
    }
    case blockTypesRecord.richText: {
      const isDeletedAndConditionIsAdded =
        condition === 'added' && change.status === 'deleted'

      return (
        <DiffBlockWrapper id={change.id} {...rest}>
          {isInFullView ? (
            <div className="h-9 flex items-center" />
          ) : (
            <DiffBlockTitle
              blockChange={change}
              condition={condition}
              link={titleAsLink && change.status !== 'deleted'}
              parentSourceBlockId={parentSourceBlockId}
            >
              {children}
            </DiffBlockTitle>
          )}
          <RichTextBlockDiff
            status={change.status}
            changesTree={changesTree}
            treeOld={treeOld}
            current={
              isDeletedAndConditionIsAdded
                ? (change.previous as RichTextBlock)
                : (change.current as RichTextBlock)
            }
            previous={change.previous as RichTextBlock | null}
            conflicting={change.conflicting as RichTextBlock | null}
            change={change}
            condition={condition}
            className={clsx(
              condition === 'added' &&
                change.status === 'deleted' &&
                'opacity-30'
            )}
          />
          {extraContentNode &&
            extraContentNode({
              wrapper: ({ children }) => <div className="mt-2">{children}</div>,
            })}
        </DiffBlockWrapper>
      )
    }
    case blockTypesRecord.reference: {
      return (
        <DiffBlockWrapper id={change.id} {...rest}>
          <DiffBlockTitle
            blockChange={change}
            condition={condition}
            link={titleAsLink}
            parentSourceBlockId={parentSourceBlockId}
          >
            {children}
          </DiffBlockTitle>
          <ReferenceBlockDiff
            change={change}
            block={change.current as ReferenceBlock}
            blockTreeManager={condition === 'added' ? treeNew : treeOld}
            condition={condition}
          />
          {extraContentNode &&
            extraContentNode({
              wrapper: ({ children }) => <div className="mt-2">{children}</div>,
            })}
        </DiffBlockWrapper>
      )
    }
    case blockTypesRecord.document:
    case blockTypesRecord.component:
    case blockTypesRecord.instance: {
      let diffedBlock: InstanceBlock
      if (change.status === 'deleted') {
        diffedBlock = change.previous as InstanceBlock
      } else if (condition === 'added') {
        diffedBlock = change.current as InstanceBlock
      } else {
        diffedBlock = {
          ...change.previous,
          id: change.current?.id,
        } as InstanceBlock
      }

      const isDeletedAndConditionIsAdded =
        condition === 'added' && change.status === 'deleted'

      return (
        <DiffBlockWrapper
          id={change.id}
          {...rest}
          className={clsx(rest.className, '!grid-rows-1')}
        >
          {change.status !== 'unmodified' && (
            <div className="flex w-full h-9 items-center">{children}</div>
          )}
          <NestedDocumentBlockLink
            mode="readonly"
            block={diffedBlock}
            prefix={tabPrefix}
            disabled={condition === 'removed' || isDeletedAndConditionIsAdded}
            wrapperClassName={clsx({
              '!bg-green-diff/20 !border-green-diff/30 hover:!bg-green-diff/30':
                change.status === 'created',
              '!bg-yellow/10 !border-yellow/15 hover:!bg-yellow/15':
                change.status === 'modified' && condition === 'added',
              '!bg-red/10 !border-red/15 hover:!bg-red/15':
                change.status === 'deleted' && condition === 'removed',
              'opacity-30': isDeletedAndConditionIsAdded,
            })}
            parentBlock={null}
          />
          {extraContentNode &&
            extraContentNode({
              wrapper: ({ children }) => <div className="mt-2">{children}</div>,
            })}
        </DiffBlockWrapper>
      )
    }
    case blockTypesRecord.media: {
      return (
        <DiffAsset
          {...rest}
          condition={condition}
          change={change}
          parentSourceBlockId={parentSourceBlockId}
          link={titleAsLink}
          extraContentNode={extraContentNode}
        >
          {children}
        </DiffAsset>
      )
    }
    case blockTypesRecord.date: {
      return (
        <DiffBlockWrapper id={change.id} {...rest}>
          <DiffBlockTitle
            blockChange={change}
            condition={condition}
            link={titleAsLink}
            parentSourceBlockId={parentSourceBlockId}
          >
            {children}
          </DiffBlockTitle>
          <DateBlockDiff change={change} condition={condition} />
          {extraContentNode &&
            extraContentNode({
              wrapper: ({ children }) => <div className="mt-2">{children}</div>,
            })}
        </DiffBlockWrapper>
      )
    }
    case blockTypesRecord.codeSnippet: {
      const current = change.current as BlockByType<'code-snippet'>
      const previous = (change.conflicting ??
        change.previous) as BlockByType<'code-snippet'>

      return (
        <DiffBlockWrapper id={change.id} {...rest}>
          <DiffBlockTitle
            blockChange={change}
            condition={condition}
            link={titleAsLink}
            parentSourceBlockId={parentSourceBlockId}
          >
            {children}
          </DiffBlockTitle>
          <DiffCodeEditor
            condition={condition}
            language={(current?.value.language as Language) ?? null}
            oldValue={previous?.value.code ?? ''}
            newValue={current?.value.code ?? ''}
          />
          {extraContentNode &&
            extraContentNode({
              wrapper: ({ children }) => <div className="mt-2">{children}</div>,
            })}
        </DiffBlockWrapper>
      )
    }
    case blockTypesRecord.select: {
      const diff = getSelectDiff({ change, condition })

      return (
        <DiffBlockWrapper id={change.id} {...rest}>
          <DiffBlockTitle
            blockChange={change}
            condition={condition}
            link={titleAsLink}
            parentSourceBlockId={parentSourceBlockId}
          >
            {children}
          </DiffBlockTitle>
          <SelectBlockDiff
            condition={condition}
            diff={diff}
            status={change.status}
          />
          {extraContentNode &&
            extraContentNode({
              wrapper: ({ children }) => <div className="mt-2">{children}</div>,
            })}
        </DiffBlockWrapper>
      )
    }
    case blockTypesRecord.color: {
      return (
        <DiffBlockWrapper id={change.id} {...rest}>
          <DiffBlockTitle
            blockChange={change}
            condition={condition}
            link={titleAsLink}
            parentSourceBlockId={parentSourceBlockId}
          >
            {children}
          </DiffBlockTitle>
          <ColorInputDiff condition={condition} blockChange={change} />
          {extraContentNode &&
            extraContentNode({
              wrapper: ({ children }) => <div className="mt-2">{children}</div>,
            })}
        </DiffBlockWrapper>
      )
    }
    case blockTypesRecord.comment: {
      const current = change.current as
        | BlockByType<'comment'>
        | undefined
        | null
      const previous = change.previous as
        | BlockByType<'comment'>
        | undefined
        | null

      let currentValue = ''
      try {
        currentValue = current
          ? jsonToPlainText(
              stateToJson(current.value.state) as Record<string, unknown>,
              commentExtensionsForTransformations
            )
          : ''
      } catch (error) {
        // noop
      }

      let previousValue = ''
      try {
        previousValue = previous
          ? jsonToPlainText(
              stateToJson(previous.value.state) as Record<string, unknown>,
              commentExtensionsForTransformations
            )
          : ''
      } catch (error) {
        // noop
      }

      const isDeletedAndConditionIsAdded =
        condition === 'added' && change.status === 'deleted'
      const diff = Diff.diffWords(
        previousValue,
        isDeletedAndConditionIsAdded ? previousValue : currentValue
      )
      const isEmpty =
        (condition === 'removed' && !previousValue) ||
        (condition === 'added' && !currentValue)

      return (
        <DiffBlockWrapper
          id={change.id}
          {...rest}
          className={clsx('text-dark-muted text-sm', rest.className)}
        >
          {change.status !== 'unmodified' && children}
          {!isEmpty ? (
            <DiffLine
              diff={diff}
              condition={condition}
              className={clsx(isDeletedAndConditionIsAdded && 'opacity-30')}
            />
          ) : (
            <i
              className={clsx(
                'text-dark-faint',
                isDeletedAndConditionIsAdded && 'opacity-30'
              )}
            >
              Empty comment
            </i>
          )}
          {extraContentNode &&
            extraContentNode({
              wrapper: ({ children }) => <div className="mt-2">{children}</div>,
            })}
        </DiffBlockWrapper>
      )
    }
    case blockTypesRecord.icon: {
      return (
        <DiffBlockWrapper id={change.id} {...rest}>
          <DiffBlockTitle
            blockChange={change}
            condition={condition}
            link={titleAsLink}
            parentSourceBlockId={parentSourceBlockId}
          >
            {children}
          </DiffBlockTitle>
          <IconBlockDiff change={change} type="text" condition={condition} />
          {extraContentNode &&
            extraContentNode({
              wrapper: ({ children }) => <div className="mt-2">{children}</div>,
            })}
        </DiffBlockWrapper>
      )
    }
    case blockTypesRecord.variantSet: {
      const variantsDiff = getVariantsDiff({ change })
      return (
        <DiffBlockWrapper id={change.id} {...rest}>
          <DiffBlockTitle
            blockChange={change}
            condition={condition}
            link={titleAsLink}
            parentSourceBlockId={parentSourceBlockId}
          >
            {children}
          </DiffBlockTitle>
          <VariantSetBlockDiff
            variantsDiff={variantsDiff}
            status={change.status}
            block={change.current as VariantSetBlock | null}
            condition={condition}
          />
          {extraContentNode &&
            extraContentNode({
              wrapper: ({ children }) => <div className="mt-2">{children}</div>,
            })}
        </DiffBlockWrapper>
      )
    }
    case blockTypesRecord.event: {
      return (
        <DiffBlockWrapper id={change.id} {...rest}>
          <DiffBlockTitle
            blockChange={change}
            condition={condition}
            link={titleAsLink}
            parentSourceBlockId={parentSourceBlockId}
          >
            {children}
          </DiffBlockTitle>
          {extraContentNode &&
            extraContentNode({
              wrapper: ({ children }) => <div className="mt-2">{children}</div>,
            })}
        </DiffBlockWrapper>
      )
    }
    case blockTypesRecord.workflow: {
      return (
        <DiffBlockWrapper id={change.id} {...rest}>
          <DiffBlockTitle
            blockChange={change}
            condition={condition}
            link={titleAsLink}
            parentSourceBlockId={parentSourceBlockId}
          >
            {children}
          </DiffBlockTitle>
          <WorkflowBlockDiff change={change} condition={condition} />
          {extraContentNode &&
            extraContentNode({
              wrapper: ({ children }) => <div className="mt-2">{children}</div>,
            })}
        </DiffBlockWrapper>
      )
    }
    default:
      return null
  }
}

export const ConflictResolutionControls = ({
  cr,
  change,
}: {
  cr: ContentRequest
  change: BlockChangeWithConflict
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const conflictResolution = cr?.conflictResolutions?.[change.id]
  const buttonClassName =
    'text-dark-muted data-[active=false]:hover:text-dark-dim data-[active=false]:hover:border-dark-muted data-[active=true]:!bg-green data-[active=true]:!border-green data-[active=true]:!text-dark-bright'

  const isJustRichTextValue =
    change.type === 'rich-text' &&
    change.conflictPaths.every((p) => p.includes('value'))

  const isConflictOnDeletion =
    change.conflictType === 'deleted-in-source' ||
    change.conflictType === 'deleted-in-head'

  const allResolved = isConflictOnDeletion
    ? !!conflictResolution
    : change.conflictPaths.every((path) => {
        if (change.type === 'rich-text' && path.includes('value')) {
          const index = path.split('.')[2]
          return cr?.conflictResolutions?.[`${change.id}/${index}`]
        } else {
          return !!cr?.conflictResolutions?.[change.id]
        }
      })

  return (
    <div className="flex gap-1 items-center justify-center z-[51] relative bg-dark-base">
      {!isJustRichTextValue && (
        <IconButton
          intent={'secondary'}
          className={buttonClassName}
          onClick={async () => {
            if (!cr) return
            await replicache?.mutate.resolveConflict({
              type: 'block',
              contentRequestId: cr.id,
              sourceBlockId: change?.id,
              resolution: conflictResolution === 'base' ? null : 'base',
            })
          }}
          data-active={conflictResolution === 'base'}
        >
          <ArrowLeftIcon />
        </IconButton>
      )}
      <TooltipPrimitive.Root delayDuration={200}>
        <TooltipPrimitive.Trigger className="p-2 cursor-default shrink-0 rounded-full hover:bg-dark-bright/20 flex items-center justify-center">
          <StatusIcon
            status={change.status}
            disableTooltip
            isConflictResolved={!!allResolved}
          />
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Content side="top" align="center" sideOffset={4}>
          {/* <TooltipPrimitive.Arrow className="fill-red-elevated/20" /> */}
          {/* <div className="text-red-elevated bg-red-elevated/20 px-3 rounded-full h-6 text-xs font-medium flex items-center gap-1">
      {change.conflictInfo}
    </div> */}
          <div className="text-dark-bright bg-dark-border-solid px-3 rounded-full h-6 text-xs font-medium flex items-center gap-1">
            Conflict on{' '}
            {change.conflictPaths.length
              ? change.conflictPaths.map((p, i) => {
                  const isLast = i === change.conflictPaths.length - 1
                  const isBeforeLast = i === change.conflictPaths.length - 2
                  return (
                    <span key={i}>
                      <span className="font-mono bg-yellow text-dark-bg px-1">
                        {p}
                      </span>
                      {isLast ? '' : isBeforeLast ? ', and ' : ', '}
                    </span>
                  )
                })
              : change.conflictType}
          </div>
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Root>
      {!isJustRichTextValue && (
        <IconButton
          intent={'secondary'}
          className={buttonClassName}
          onClick={async () => {
            if (!cr) return
            await replicache?.mutate.resolveConflict({
              type: 'block',
              contentRequestId: cr.id,
              sourceBlockId: change?.id,
              resolution: conflictResolution === 'source' ? null : 'source',
            })
          }}
          data-active={conflictResolution === 'source'}
        >
          <ArrowRightIcon />
        </IconButton>
      )}
    </div>
  )
}
